import type { ChildrenOnly } from '@studio/design-system/props';
import { normalizeId } from '@studio/models';
import { parseQuery } from '@studio/models/net';
import type { Room as M_Room } from '@studio/models/room';
import { useLazyRef } from '@studio/react-utils/hooks';
import { toBoolean } from '@tryiris-ai/js-types';
import { useEffect, useLayoutEffect, useRef } from 'react';
import type { ReactElement } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { Props } from './types.js';
import { broker } from '#~/agent/index.js';
import { Provider as AgentProvider } from '#~/agent/provider/index.js';
import { useDepot } from '#~/depot/hooks.js';
import { NotFoundError } from '#~/error/index.js';
import { useLastRoomIdPref } from '#~/room/hooks.js';
import { Provider as RoomProvider } from '#~/room/provider/index.js';
import { useAgents } from '#~/agent/hooks.js';
import { shallow } from 'zustand/shallow';
import { TimeoutWatchdog } from './timeout-watchdog.js';
import type { Agent } from '@studio/models/agent';
import { AutoLinkLoadingScreen } from '../verify-auto-link/v2/auto-link-loading.js';
import { InitialBillingGate } from './initial-billing-gate.js';

// Per y4fp5-W1 (seamless-load): the BootOverlay above this route fades
// to opacity-0 once autolink+root+registry are satisfied, but room data
// and agent connection are still hydrating. Using a chrome-identical
// loading screen here means the overlay's fade reveals byte-identical
// chrome underneath — no "middle no-text Loader" flash. The synthetic
// `idle` state lands on AutoLinkLoadingScreen's default copy
// ("Loading your studio…").
const SEAMLESS_LOAD_STATE = { status: 'idle' as const };

const TIMEOUT_WATCHDOG_TIMEOUT = 17_000;

const RoomBootWaitingReadinessPublisher = ({ roomId }: { readonly roomId: M_Room['id'] }): null => {
  const produce = useDepot((µ) => µ.produce);

  // Per ai.knowledge/global `code-comments-required`: RoomBoot is a transient
  // wait-and-navigate route, so it holds the overlay until Room publishes that
  // final room UI or offline placeholder is actually visible.
  useLayoutEffect(() => {
    produce((draft) => {
      draft.boot.isRootReady = false;
    });
  }, [produce, roomId]);

  return null;
};

const Boot = ({}: Props & { roomId: M_Room['id'] }): ReactElement => {
  const agentIds = useAgents((µ) => µ.map((a) => a.id), shallow);
  const statuses = useAgents((µ) => µ.map((a) => a.status), shallow);
  const data: Record<string, any> = {};
  const redirectedRef = useRef(false);

  for (const agentId of agentIds) {
    const proxy = broker.acquire(agentId);
    data['State ' + agentId] = proxy?.state;
  }

  const navigate = useNavigate();

  // Wait for at least one agent to be non-offline before navigating.
  // During fresh startup, agents start as "offline" because state sync
  // hasn't connected yet. Navigating before agents connect causes the
  // room Guard to render Placeholder instead of the full studio UI.
  const hasConnectedAgent = statuses.some((s) => s !== 'offline');

  useEffect(() => {
    if (!hasConnectedAgent) return;

    for (const key in data) {
      const state = data[key];

      if (Object.keys(state).length > 0) {
        // take the current url but drop /boot from it
        const path = window.location.pathname.split('/').slice(0, -1).join('/');

        if (redirectedRef.current) {
          return;
        }

        redirectedRef.current = true;

        navigate(path);
      }
    }
  }, [agentIds, data, navigate, hasConnectedAgent]);

  // Per y4fp5-W1: chrome-identical loading shell (see SEAMLESS_LOAD_STATE).
  return <AutoLinkLoadingScreen state={SEAMLESS_LOAD_STATE} />;
};

const Inner = ({ roomId, ...props }: Props & { roomId: M_Room['id'] }): ReactElement => {
  // Delay page load until room data is ready.
  const isReady = useDepot((µ) => !!µ.rooms[roomId]);

  return (
    <>
      {isReady ? (
        <Boot roomId={roomId} {...props} />
      ) : (
        <AutoLinkLoadingScreen state={SEAMLESS_LOAD_STATE} />
      )}
    </>
  );
};

/** Room guard. */
const Guard = ({ children }: ChildrenOnly & { roomId: M_Room['id'] }): ReactElement => {
  // If the agent is online, cool.  Otherwise show a placeholder.
  const statuses = useAgents((µ) => µ.map((a) => a.status), shallow);
  const allOffline = statuses.length > 0 && statuses.every((s) => s === 'offline');

  return allOffline ? <AutoLinkLoadingScreen state={SEAMLESS_LOAD_STATE} /> : <>{children}</>;
};

/** `<Room>` route. */
export const RoomBoot = (props: Props): ReactElement => {
  const params = useParams();

  const roomId = normalizeId<M_Room['id']>(params.roomId);

  const [_, setLastRoomId] = useLastRoomIdPref();

  useEffect(() => {
    setLastRoomId(roomId);
  }, [roomId]);

  if (!roomId) {
    throw new NotFoundError();
  }

  const options = useLazyRef(() => {
    // Parse query string.
    const params = parseQuery(window.location.search);

    return {
      restore: toBoolean(params['agent:restore'], { default: true })
    };
  }).current;

  return (
    <InitialBillingGate roomId={roomId}>
      <RoomProvider options={options} roomId={roomId}>
        <RoomBootWaitingReadinessPublisher roomId={roomId} />
        <AgentProvider roomId={roomId} noWatchdog={true}>
          <TimeoutWatchdog
            agentIDs={Object.keys(broker) as Agent['id'][]}
            timeout={TIMEOUT_WATCHDOG_TIMEOUT}
          />
          <Guard roomId={roomId}>
            <Inner roomId={roomId} {...props} />
          </Guard>
        </AgentProvider>
      </RoomProvider>
    </InitialBillingGate>
  );
};
